import { FetchCacheItemsSources } from '../../../../types';
import {
  FetchTasksPriceCacheKey,
  FetchTasksPriceFilters,
  FetchTasksPriceGqlQuery,
  FetchTasksPriceLimit,
  FetchTasksPricePage,
  FetchTasksPriceSort
} from '../../tasksPriceTypes';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_TASKS_PRICE_FILTERS,
  INITIAL_TASKS_PRICE_LIMIT,
  INITIAL_TASKS_PRICE_PAGE,
  INITIAL_TASKS_PRICE_SORT
} from '../../tasksPriceConstants';
import { TasksPermissions } from '../../../tasks/tasksConstants';

interface CachePaginatedTasksPriceOptions {
  cacheKey: FetchTasksPriceCacheKey;
  initialSort?: FetchTasksPriceSort;
  initialPage?: FetchTasksPricePage;
  initialLimit?: FetchTasksPriceLimit;
  initialFilters?: FetchTasksPriceFilters;
  options?: {
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
  query: FetchTasksPriceGqlQuery;
}

const scope = 'tasksPrice';

function useCachePaginatedTasksPrice<ItemType>({
  cacheKey,
  initialFilters = INITIAL_TASKS_PRICE_FILTERS,
  initialSort = INITIAL_TASKS_PRICE_SORT,
  initialPage = INITIAL_TASKS_PRICE_PAGE,
  initialLimit = INITIAL_TASKS_PRICE_LIMIT,
  options = {},
  query
}: CachePaginatedTasksPriceOptions) {
  // DB until cache #2316's formula fix is re-projected into the ES index on every tier; ES also refuses client users.
  const source = FetchCacheItemsSources.DB;

  const {
    changeItemsFilters,
    clearItemsFilters,
    currentFilters,
    currentLimit,
    currentPage,
    currentSort,
    data,
    filterItems,
    isFetched,
    isLoading,
    isPlaceholderData,
    items,
    itemsError,
    itemsTotalCount,
    limitItems,
    paginateItems,
    prefetchItems,
    sortItems,
    updateItemCache
  } = useCacheIndexQuery<ItemType>({
    action: TasksPermissions.READ_TASKS_TABLE_PRICE_FIELD,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    options,
    query,
    scope,
    source
  });

  return {
    changeTasksPriceFilters: changeItemsFilters,
    clearTasksPriceFilters: clearItemsFilters,
    filterTasksPrice: filterItems,
    tasksPrice: items,
    tasksPriceData: data,
    tasksPriceError: itemsError,
    tasksPriceFetched: isFetched,
    tasksPriceFilters: currentFilters,
    tasksPriceIsPlaceholderData: isPlaceholderData,
    tasksPriceLimit: currentLimit,
    tasksPriceLoading: isLoading,
    tasksPricePage: currentPage,
    tasksPriceSort: currentSort,
    tasksPriceTotalCount: itemsTotalCount,
    limitTasksPrice: limitItems,
    paginateTasksPrice: paginateItems,
    prefetchTasksPrice: prefetchItems,
    sortTasksPrice: sortItems,
    updateTaskPriceCache: updateItemCache
  };
}

export default useCachePaginatedTasksPrice;
